import React, { useState, useMemo, useEffect, useRef } from "react";
import { loadModules } from "esri-loader";
import TopHeader from "../components/layout/TopHeader";
import SidePanel from "../components/layout/SidePanel";
import MapView from "../components/map/MapView";
import IncidentMapView from "../components/map/IncidentMapView";
import { useBusSystem } from "../hooks/useBusSystem";
import { getBusById } from "../services/busService";
import { useAuth } from "../context/AuthContext";
import { revertIncident } from "../services/reportsService";
import { etaWebsocketService } from "../services/websocket";
import StopDetailsPanel from "../components/tracking/StopDetailsPanel";
import BusDetailsModal from "../components/tracking/BusDetailsModal";
import RevertIncidentModal from "../components/tracking/RevertIncidentModal";
import CityCard from "../components/tracking/CityCard";
import IncidenciasPanel from "../components/tracking/IncidenciasPanel";

// Nuevos servicios importados para la validación del gobierno
import { getProfileService } from "../services/profileService";
import { getAllRouteSuperRoutes } from "../services/cmsService";
import { useActiveRole } from "../context/ActiveRoleContext";

const NO_LINE = { surId: null, surName: "Sin línea" };

// Junta las rutas por nombre (varios rouId pueden compartirlo) y las separa en activas/inactivas
// segun tengan buses transmitiendo. Sirve igual para una ciudad entera que para una linea.
function summarizeRoutes(routes, activeBuses) {
  const groupedRoutesMap = {};
  routes.forEach(route => {
      const name = route.rouName;
      if (!groupedRoutesMap[name]) groupedRoutesMap[name] = { rouName: name, routeIds: [], activeBuses: [] };
      groupedRoutesMap[name].routeIds.push(route.rouId);
      const busesForThisId = Object.values(activeBuses).filter(b => Number(b.rouId) === route.rouId);
      groupedRoutesMap[name].activeBuses.push(...busesForThisId);
  });

  const activeRoutes = [];
  const inactiveRoutes = [];
  let activeRoutesCount = 0;
  let activeBusesCount = 0;

  Object.values(groupedRoutesMap).forEach(group => {
      if (group.activeBuses.length > 0) {
          activeRoutesCount++;
          activeBusesCount += group.activeBuses.length;
          activeRoutes.push(group);
      } else {
          inactiveRoutes.push(group);
      }
  });

  return {
      routeIds: routes.map(r => r.rouId),
      activeRoutes, inactiveRoutes, activeRoutesCount, activeBusesCount,
      totalRoutes: Object.keys(groupedRoutesMap).length
  };
}

export default function TrackingPage() {
  const [activeTab, setActiveTab] = useState("general");
  const [incidentVisibility, setIncidentVisibility] = useState({});
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [loadingCities, setLoadingCities] = useState(new Set());
  const staticStopsLayerRef = useRef(null); 
  const initializedRoutes = useRef(new Set());
  const prevActiveRouteIds = useRef(new Set());

  const [busDetails, setBusDetails] = useState(null);
  const [isBusModalOpen, setIsBusModalOpen] = useState(false);
  const [isLoadingBus, setIsLoadingBus] = useState(false);
  
  const [isRevertIncidentModalOpen, setIsRevertIncidentModalOpen] = useState(false);
  const [incidentToRevert, setIncidentToRevert] = useState(null);
  const [isReverting, setIsReverting] = useState(false);
  const [revertStatus, setRevertStatus] = useState(null); 
  
  const mapContainerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [realTimeEtas, setRealTimeEtas] = useState([]);
  
  const { token } = useAuth(); 
  const { 
      mapRef, cities, selectedCities, isConnected, toggleCity,
      hiddenRoutes, toggleRouteGroup, setAllowedRoutes, incidents,
      activeBuses, focusOnLocation,
      busPopup, clickedBusId, setClickedBusId,
      permissibleData, clickedStop, setClickedStop 
  } = useBusSystem();

  // =====================================================================
  // INICIO: LÓGICA DE FILTRADO POR PERFIL DE GOBIERNO
  // =====================================================================
  const { isGovernmentView } = useActiveRole();
  const [isGovUser, setIsGovUser] = useState(false);
  const [myGovId, setMyGovId] = useState(null);
  const [allowedRouteIds, setAllowedRouteIds] = useState([]);
  const [loadingGovPermissions, setLoadingGovPermissions] = useState(true);

  // 0. Relacion ruta <-> linea para TODOS los roles: con ella se agrupa el panel por linea y, en
  // vista de alcaldia, se decide que rutas se ven. null = todavia cargando; si la peticion falla
  // queda [] y todas las rutas caen en "Sin línea".
  const [routeSuperRoutes, setRouteSuperRoutes] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    getAllRouteSuperRoutes(token).then((data) => {
      if (!cancelled) setRouteSuperRoutes(Array.isArray(data) ? data : []);
    });
    return () => { cancelled = true; };
  }, [token]);

  // 1. Obtener los permisos basados en el ROL ACTIVO + el perfil
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!token) {
        setLoadingGovPermissions(false);
        return;
      }

      // Manda el rol ELEGIDO, no los que el usuario tiene: alguien con EMPLOYEE +
      // GOVERNMENT_ADMIN (caso real) ve TODO si entro como Personal, y solo las lineas de su
      // alcaldia si entro como Gobierno. Antes esto se decidia por la sola existencia de
      // profile.government, asi que a ese usuario se le filtraba siempre.
      if (!isGovernmentView) {
        setIsGovUser(false);
        setMyGovId(null);
        setAllowedRouteIds([]);
        setLoadingGovPermissions(false);
        return;
      }

      // Sin la relacion ruta <-> linea (paso 0) no hay con que filtrar: se espera a que llegue.
      if (routeSuperRoutes === null) {
        setLoadingGovPermissions(true);
        return;
      }

      try {
        setLoadingGovPermissions(true);
        const profile = await getProfileService(token);
        const govId = profile?.government?.govId;

        // Rol de alcaldia pero sin alcaldia enlazada (le falta la fila GovernmentEmployee): NO se
        // cae a "ve todo", que es justo el bug que esconde ese dato faltante. Sin alcaldia no hay
        // lineas que mostrar.
        if (!govId) {
          console.warn(
            "Rol de alcaldía sin Government asociado: el perfil no trae government. " +
            "Falta la fila GovernmentEmployee del usuario."
          );
          setIsGovUser(true);
          setMyGovId(null);
          setAllowedRouteIds([]);
          setLoadingGovPermissions(false);
          return;
        }

        setIsGovUser(true);
        setMyGovId(govId);

        // La alcaldia dueña de la linea vive en la linea misma: SuperRoute.surGovId, que sale
        // serializado como `govId` (ELP-1110). Asi que la misma peticion del paso 0 trae ruta +
        // linea + alcaldia, y el filtro es un === contra mi govId.
        //
        // Antes esto eran 1 + N peticiones (la tabla puente GovernmentsSuperRoutes y despues una
        // por cada linea de la alcaldia) y dependia de que la puente estuviera al dia.
        //
        // govId null = linea INDEPENDIENTE (sin alcaldia): no es de nadie, asi que no entra. El
        // === tambien la descarta sin ayuda, pero se deja explicito porque es el caso que se
        // rompe solo si alguien cambia esta condicion.
        const routeIdsSet = new Set();
        routeSuperRoutes.forEach((item) => {
          const lineGovId = item.superRoute?.govId;
          if (lineGovId == null || lineGovId !== govId) return;
          if (item.rosIsDeleted) return;
          if (!item.route?.rouEnabled) return;
          routeIdsSet.add(item.route.rouId);
        });

        setAllowedRouteIds(Array.from(routeIdsSet));
      } catch (error) {
        console.error("❌ Error resolviendo permisos de rutas de gobierno:", error);
        setIsGovUser(false);
        setAllowedRouteIds([]);
      } finally {
        setLoadingGovPermissions(false);
      }
    };

    fetchPermissions();
    // Cambiar de rol en caliente recalcula los permisos sin recargar la pagina.
  }, [token, isGovernmentView, routeSuperRoutes]);

  // 2. Filtramos la data de permissibleData que viene del hook global. Se espera tambien a la
  // relacion ruta <-> linea para que el panel no se pinte sin agrupar y luego salte.
  const filteredPermissibleData = useMemo(() => {
    if (loadingGovPermissions || !permissibleData || routeSuperRoutes === null) return null;
    if (!isGovUser) return permissibleData; // Usuario normal ve todo

    const filtered = {};
    Object.entries(permissibleData).forEach(([citIdStr, routes]) => {
      const validRoutes = routes.filter(r => allowedRouteIds.includes(r.rouId));
      if (validRoutes.length > 0) {
        filtered[citIdStr] = validRoutes;
      }
    });
    return filtered;
  }, [permissibleData, isGovUser, allowedRouteIds, loadingGovPermissions, routeSuperRoutes]);

  // 3. El mapa solo pinta buses y paradas de las rutas permitidas. Antes se metian las no
  // autorizadas en hiddenRoutes una sola vez, y eso corria con permissibleData aun vacio ({}), asi
  // que no ocultaba nada; y los buses sin ruta asignada (rouId null) nunca entraban ahi.
  // null = sin filtro (vista Personal); [] = nada mientras se resuelven los permisos.
  const mapRouteIds = useMemo(() => {
    if (!isGovernmentView) return null;
    if (loadingGovPermissions) return [];
    return isGovUser ? allowedRouteIds : null;
  }, [isGovernmentView, loadingGovPermissions, isGovUser, allowedRouteIds]);

  useEffect(() => {
    setAllowedRoutes(mapRouteIds);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRouteIds]);

  // 4. Filtramos las incidencias
  const displayIncidents = useMemo(() => {
    if (loadingGovPermissions) return [];
    if (!isGovUser) return incidents;
    return incidents.filter(inc => allowedRouteIds.includes(inc.rouId));
  }, [incidents, isGovUser, allowedRouteIds, loadingGovPermissions]);
  // =====================================================================
  // FIN: LÓGICA DE FILTRADO POR PERFIL DE GOBIERNO
  // =====================================================================


  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      if (mapContainerRef.current) mapContainerRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Ahora se usa filteredPermissibleData
  useEffect(() => {
      if (!filteredPermissibleData || Object.keys(filteredPermissibleData).length === 0) return;
      const idsToHide = [];
      const idsToShow = [];

      Object.values(filteredPermissibleData).forEach(routes => {
          routes.forEach(r => {
              const rouId = r.rouId;
              const hasActive = Object.values(activeBuses).some(b => Number(b.rouId) === rouId);
              
              if (!initializedRoutes.current.has(rouId)) {
                  initializedRoutes.current.add(rouId); 
                  if (!hasActive) idsToHide.push(rouId);
                  if (hasActive) prevActiveRouteIds.current.add(rouId);
              } else {
                  if (hasActive && !prevActiveRouteIds.current.has(rouId)) {
                      prevActiveRouteIds.current.add(rouId);
                      idsToShow.push(rouId);
                  } else if (!hasActive && prevActiveRouteIds.current.has(rouId)) {
                      prevActiveRouteIds.current.delete(rouId);
                  }
              }
          });
      });

      if (idsToHide.length > 0) toggleRouteGroup(idsToHide, true);
      if (idsToShow.length > 0) toggleRouteGroup(idsToShow, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPermissibleData, activeBuses]);

  // rouId -> lineas (SuperRoute) de las que cuelga. Una ruta puede estar en varias lineas (la
  // tabla puente no lo impide) y sale en cada una; en vista de alcaldia solo cuentan las de MI
  // alcaldia, para no mostrarle lineas ajenas.
  const linesByRouteId = useMemo(() => {
      const map = new Map();
      (routeSuperRoutes || []).forEach((item) => {
          if (item.rosIsDeleted || !item.route || !item.superRoute) return;
          if (isGovUser && item.superRoute.govId !== myGovId) return;
          const { rouId } = item.route;
          const { surId, surName } = item.superRoute;
          if (!map.has(rouId)) map.set(rouId, []);
          const lines = map.get(rouId);
          if (!lines.some(l => l.surId === surId)) lines.push({ surId, surName: surName || `Línea ${surId}` });
      });
      return map;
  }, [routeSuperRoutes, isGovUser, myGovId]);

  // Ciudad -> linea -> ruta -> buses. Los contadores de la ciudad salen de todas sus rutas (no de
  // sumar las lineas) para no contar dos veces una ruta que esta en mas de una linea.
  const processedCities = useMemo(() => {
      const allCities = [];
      if (!filteredPermissibleData) return allCities;

      Object.entries(cities).forEach(([citIdStr, cityName]) => {
          const citId = Number(citIdStr);
          const cityRoutes = filteredPermissibleData[citId];
          if (!cityRoutes) return; // Saltamos la ciudad si ya no tiene rutas tras el filtro

          const routesByLine = new Map();
          cityRoutes.forEach(route => {
              const routeLines = linesByRouteId.get(route.rouId);
              (routeLines && routeLines.length > 0 ? routeLines : [NO_LINE]).forEach(line => {
                  if (!routesByLine.has(line.surId)) routesByLine.set(line.surId, { ...line, routes: [] });
                  routesByLine.get(line.surId).routes.push(route);
              });
          });

          // Primero las lineas con buses transmitiendo, luego alfabetico; "Sin línea" siempre al final.
          const lines = Array.from(routesByLine.values())
              .map(({ routes, ...line }) => ({ ...line, ...summarizeRoutes(routes, activeBuses) }))
              .sort((a, b) => {
                  if ((a.surId == null) !== (b.surId == null)) return a.surId == null ? 1 : -1;
                  if ((a.activeBusesCount > 0) !== (b.activeBusesCount > 0)) return a.activeBusesCount > 0 ? -1 : 1;
                  return a.surName.localeCompare(b.surName);
              });

          allCities.push({ citId, cityName, lines, ...summarizeRoutes(cityRoutes, activeBuses) });
      });
      allCities.sort((a, b) => a.cityName.localeCompare(b.cityName));
      return allCities;
  }, [cities, filteredPermissibleData, activeBuses, linesByRouteId]);

  // Ahora se usa filteredPermissibleData para los marcadores del mapa
  useEffect(() => {
      if (!mapRef.current || !mapRef.current.map || !filteredPermissibleData || Object.keys(filteredPermissibleData).length === 0) return;

      loadModules(["esri/layers/GraphicsLayer", "esri/Graphic"]).then(([GraphicsLayer, Graphic]) => {
          if (!staticStopsLayerRef.current) {
              const newLayer = new GraphicsLayer({ id: "permissible-stops-layer" });
              mapRef.current.map.add(newLayer);
              staticStopsLayerRef.current = newLayer;
          } else {
              if (staticStopsLayerRef.current.graphics.length > 0) return;
          }

          const layer = staticStopsLayerRef.current;
          layer.removeAll();
          const graphicsToAdd = [];

          Object.entries(filteredPermissibleData).forEach(([citIdStr, routes]) => {
              const citId = Number(citIdStr);
              routes.forEach(route => {
                  route.stops.forEach(stop => {
                      const lat = stop.stoLatitude || stop.lat;
                      const lon = stop.stoLongitude || stop.lon;
                      if (!lat || !lon) return;

                      const pt = new Graphic({
                          geometry: { type: "point", latitude: lat, longitude: lon },
                          symbol: { type: "simple-marker", style: "circle", color: "white", size: "10px", outline: { color: "#10B981", width: 2 } },
                          attributes: { type: "permissible-stop", name: stop.stoName || stop.name, rouId: route.rouId, citId: citId,  stoId: stop.stoId || stop.id },
                          visible: false 
                      });
                      graphicsToAdd.push(pt);
                  });
              });
          });
          layer.addMany(graphicsToAdd);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPermissibleData, mapRef]); 

  useEffect(() => {
      if (staticStopsLayerRef.current && staticStopsLayerRef.current.graphics) {
          staticStopsLayerRef.current.graphics.forEach(graphic => {
              if (graphic.attributes && graphic.attributes.rouId) {
                  const isCitySelected = selectedCities.has(graphic.attributes.citId);
                  const isRouteOn = !hiddenRoutes.has(graphic.attributes.rouId);
                  graphic.visible = isCitySelected && isRouteOn;
              }
          });
      }
  }, [selectedCities, hiddenRoutes]);

  useEffect(() => {
      if (!token) return;

      // Conectamos
      etaWebsocketService.connect(token);

      const unsubscribe = etaWebsocketService.subscribe((response) => {
          // 🔥 AHORA VALIDAMOS QUE LA RESPUESTA SEA UN ARRAY DIRECTAMENTE 🔥
          if (Array.isArray(response)) {
              setRealTimeEtas(response); // Guardamos el array crudo tal cual llega
          }
      });

      return () => {
          unsubscribe();
          etaWebsocketService.disconnect();
      };
  }, [token]);
  
  const openBusDetails = async (busId) => {
    setIsLoadingBus(true); setIsBusModalOpen(true); 
    try {
      const data = await getBusById(busId, token);
      setBusDetails(data);
    } catch (error) {
      console.error("Error al obtener los detalles:", error);
      setBusDetails(null);
    } finally {
      setIsLoadingBus(false);
    }
  };

  // Un solo Set para lo desplegado del panel (lineas y rutas); la clave la arma CityCard/LineGroup.
  const toggleExpanded = (key) => {
    setExpandedKeys(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
  };

  useEffect(() => {
    setIncidentVisibility(prev => {
        const next = { ...prev };
        let changed = false;
        displayIncidents.forEach(inc => {
            if (!next[inc.rouId]) { next[inc.rouId] = { original: true, new: true }; changed = true; }
        });
        return changed ? next : prev;
    });
  }, [displayIncidents]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (clickedBusId) { openBusDetails(clickedBusId); setClickedBusId(null); } }, [clickedBusId]);

  const toggleIncidentPath = (rouId, type) => setIncidentVisibility(prev => ({ ...prev, [rouId]: { ...prev[rouId], [type]: !prev[rouId][type] } }));

  const handleIncidentGroupToggle = (cityIncidents) => {
    const isAnyActive = cityIncidents.some(inc => {
        const vis = incidentVisibility[inc.rouId] || { original: true, new: true };
        return vis.original || vis.new;
    });
    setIncidentVisibility(prev => {
        const next = { ...prev };
        cityIncidents.forEach(inc => { next[inc.rouId] = { original: !isAnyActive, new: !isAnyActive }; });
        return next;
    });
  };

  const groupedIncidents = useMemo(() => {
    const groups = {};
    displayIncidents.forEach(inc => {
        const cityName = cities[inc.citId] || `Ciudad (ID: ${inc.citId})`;
        const routeName = inc.routeName || `Ruta (ID: ${inc.rouId})`;
        if (!groups[cityName]) groups[cityName] = [];
        groups[cityName].push({ ...inc, routeName });
    });
    return groups;
  }, [displayIncidents, cities]);

  const incidentsToDraw = displayIncidents.map(inc => {
    const vis = incidentVisibility[inc.rouId] || { original: true, new: true };
    return { ...inc, originalPath: vis.original ? inc.originalPath : null, newPath: vis.new ? inc.newPath : null };
  });

  const confirmRevertIncident = async () => {
      if (!incidentToRevert) return;
      setIsReverting(true); setRevertStatus(null);
      try {
          await revertIncident(incidentToRevert.rouId, token);
          setRevertStatus('success');
      } catch (error) { setRevertStatus('error'); } 
      finally { setIsReverting(false); }
  };

  return (
    <>
      <TopHeader title="Monitoreo" />
      
      <div className="controls-area">
        <div className="tabs-container">
          <div className={`tab-item ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>Monitoreo</div>
          <div className={`tab-item ${activeTab === 'incidencias' ? 'active' : ''}`} onClick={() => setActiveTab('incidencias')}>
            Incidencias {displayIncidents.length > 0 && (<span style={{ marginLeft: 8, background: '#EF4444', color: 'white', borderRadius: 10, padding: '2px 6px', fontSize: 10, fontWeight: 'bold' }}>{displayIncidents.length}</span>)}
          </div>
        </div>
      </div>

      <div className="content-grid">
        <SidePanel title={activeTab === "general" ? "Ciudades" : "Incidencias Detectadas"} isConnected={isConnected}>
          
          {activeTab === "general" && (
            <>
              {(!filteredPermissibleData || Object.keys(filteredPermissibleData).length === 0) ? (
                <div className="mon-tree" aria-busy="true">
                  {[0, 1, 2].map(i => <div key={i} className="mon-skeleton" style={{ opacity: 1 - i * 0.25 }} />)}
                  <p className="mon-loading-text">
                    {loadingGovPermissions ? "Validando permisos del usuario..." : "Cargando rutas..."}
                  </p>
                </div>
              ) : (
                <div className="mon-tree">
                  {processedCities.map(city => (
                      <CityCard 
                          key={city.citId}
                          cityObj={city} 
                          hiddenRoutes={hiddenRoutes} 
                          selectedCities={selectedCities} 
                          loadingCities={loadingCities} 
                          setLoadingCities={setLoadingCities} 
                          toggleRouteGroup={toggleRouteGroup} 
                          toggleCity={toggleCity} 
                          expandedKeys={expandedKeys} 
                          toggleExpanded={toggleExpanded} 
                          openBusDetails={openBusDetails} 
                          focusOnLocation={focusOnLocation} 
                      />
                  ))}
                </div>
              )}
            </>
          )}
          
          {activeTab === "incidencias" && (
             <IncidenciasPanel 
                 displayIncidents={displayIncidents}
                 groupedIncidents={groupedIncidents}
                 incidentVisibility={incidentVisibility}
                 handleIncidentGroupToggle={handleIncidentGroupToggle}
                 toggleIncidentPath={toggleIncidentPath}
                 handleOpenRevertIncidentModal={(inc) => { setIncidentToRevert(inc); setRevertStatus(null); setIsRevertIncidentModalOpen(true); }}
             />
          )}
        </SidePanel>
        
        <div ref={mapContainerRef} style={{ flex: 1, position: 'relative', width: "100%", height: "100%", backgroundColor: '#f9fafb' }}>
          <button 
            onClick={toggleFullscreen} 
            style={{ 
              position: 'absolute', bottom: 30, right: 16, zIndex: 9999, 
              backgroundColor: 'white', border: '1px solid #E5E7EB', 
              borderRadius: '8px', padding: '8px', cursor: 'pointer', 
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#4B5563', transition: 'background-color 0.2s, color 0.2s'
            }} 
            title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; e.currentTarget.style.color = '#111827'; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.color = '#4B5563'; }}
          >
            {isFullscreen ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
              </svg>
            )}
          </button>

          <div style={{ position: 'absolute', top: 0, left: 0, width: "100%", height: "100%", visibility: activeTab === "general" ? "visible" : "hidden", opacity: activeTab === "general" ? 1 : 0, pointerEvents: activeTab === "general" ? "auto" : "none", zIndex: activeTab === "general" ? 10 : 1, display: "flex", transition: "opacity 0.2s ease" }}>
            <MapView mapRef={mapRef} />
            {busPopup && activeTab === "general" && (
                <div style={{ position: 'absolute', left: busPopup.x, top: busPopup.y, transform: 'translate(-50%, -100%)', backgroundColor: 'white', padding: '8px 12px', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.15)', pointerEvents: 'none', zIndex: 50, border: `2px solid ${busPopup.color || '#3B82F6'}`, minWidth: '120px' }}>
                    <h4 style={{ margin: 0, fontSize: '11px', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 700 }}>{busPopup.title}</h4>
                    <p style={{ margin: '2px 0 0 0', fontSize: '13px', fontWeight: 700, color: '#1F2937' }}>{busPopup.subtitle}</p>
                    <div style={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%) rotate(45deg)', width: 10, height: 10, backgroundColor: 'white', borderRight: `2px solid ${busPopup.color || '#3B82F6'}`, borderBottom: `2px solid ${busPopup.color || '#3B82F6'}` }}></div>
                </div>
            )}

            <StopDetailsPanel 
                stop={clickedStop} 
                onClose={() => setClickedStop(null)}
                openBusDetails={openBusDetails} 
                realTimeEtas={realTimeEtas}
            />

          </div>
          
          <div style={{ position: 'absolute', top: 0, left: 0, width: "100%", height: "100%", visibility: activeTab === "incidencias" ? "visible" : "hidden", opacity: activeTab === "incidencias" ? 1 : 0, pointerEvents: activeTab === "incidencias" ? "auto" : "none", zIndex: activeTab === "incidencias" ? 10 : 1, display: "flex", transition: "opacity 0.2s ease" }}>
            <IncidentMapView incidents={incidentsToDraw} />
          </div>
          
          {/* 🔥 MODALES MOVIDOS AQUÍ DENTRO PARA QUE SE VEAN EN FULLSCREEN 🔥 */}
          <BusDetailsModal 
              isOpen={isBusModalOpen} 
              onClose={() => { setIsBusModalOpen(false); setTimeout(() => setBusDetails(null), 200); }} 
              isLoading={isLoadingBus} 
              busDetails={busDetails} 
          />

          <RevertIncidentModal 
              isOpen={isRevertIncidentModalOpen} 
              onClose={() => { setIsRevertIncidentModalOpen(false); setTimeout(() => { setIncidentToRevert(null); setRevertStatus(null); }, 300); }} 
              onConfirm={confirmRevertIncident} 
              incidentToRevert={incidentToRevert} 
              isReverting={isReverting} 
              revertStatus={revertStatus} 
          />

        </div>
      </div>
    </>
  );
}